import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, DollarSign, Users, Briefcase, Calendar, Phone, Mail, MapPin, Save, X, FileText, CheckCircle, AlertCircle, Home, Package, TrendingUp, LogOut, Shield, BarChart3, Warehouse, ClipboardList, ChevronLeft, ChevronRight, CreditCard } from 'lucide-react';

// Phone mask helpers
const formatPhoneInput = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const formatPhoneDisplay = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return phone;
};

const fmtMoney = (val, decimals = 2) => {
  const n = Number(val) || 0;
  return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const MobelCabinetManager = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [financeTab, setFinanceTab] = useState('receivables');
  const [finSummary, setFinSummary] = useState({ totalReceipts: 0, totalPayments: 0, net: 0, overdueCount: 0, overdueAmount: 0 });
  const [finTransactions, setFinTransactions] = useState([]);
  const [finCategories, setFinCategories] = useState([]);
  const [finCounterparties, setFinCounterparties] = useState([]);
  const [finBankAccounts, setFinBankAccounts] = useState([]);
  const [finMovements, setFinMovements] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [scheduleEvents, setScheduleEvents] = useState([]);
  const [reportsData, setReportsData] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [customerFilter, setCustomerFilter] = useState('all');

  const authFetch = async (url, options = {}) => {
    const token = localStorage.getItem('mobel_auth_token');
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
    };
    const res = await fetch(url, { ...options, headers });
    if (res.status === 401) {
      localStorage.removeItem('mobel_auth_token');
      setIsAuthenticated(false);
      throw new Error('Session expired. Please log in again.');
    }
    return res;
  };

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('mobel_auth_token');
      if (!token) {
        setIsAuthenticated(false);
        setAuthLoading(false);
        return;
      }
      try {
        const res = await fetch('/api/auth/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const data = await res.json();
        if (data.valid) {
          setIsAuthenticated(true);
          setUserRole(data.role || 'admin');
        } else {
          localStorage.removeItem('mobel_auth_token');
          setIsAuthenticated(false);
        }
      } catch {
        setIsAuthenticated(false);
      } finally {
        setAuthLoading(false);
      }
    };
    checkAuth();
  }, []);

  // Load data from database when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const [customersRes, employeesRes, estimatesRes, projectsRes] = await Promise.all([
          authFetch('/api/customers'),
          authFetch('/api/employees'),
          authFetch('/api/estimates'),
          authFetch('/api/projects'),
        ]);
        if (!customersRes.ok || !employeesRes.ok || !estimatesRes.ok || !projectsRes.ok) {
          throw new Error('Failed to fetch data');
        }
        setCustomers(await customersRes.json());
        setEmployees(await employeesRes.json());
        setEstimates(await estimatesRes.json());
        setProjects(await projectsRes.json());

        if (userRole === 'admin') {
          try {
            const usersRes = await authFetch('/api/users');
            if (usersRes.ok) setUsers(await usersRes.json());
          } catch {}
        }
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAuthenticated, userRole]);

  // Load finance data
  const loadFinanceData = async () => {
    try {
      const [sumRes, txRes, catRes, cpRes, baRes, bmRes] = await Promise.all([
        authFetch('/api/finance/summary'),
        authFetch('/api/finance/transactions'),
        authFetch('/api/finance/categories'),
        authFetch('/api/finance/counterparties'),
        authFetch('/api/finance/bank-accounts'),
        authFetch('/api/finance/bank-movements'),
      ]);
      if (sumRes.ok) setFinSummary(await sumRes.json());
      if (txRes.ok) setFinTransactions(await txRes.json());
      if (catRes.ok) setFinCategories(await catRes.json());
      if (cpRes.ok) setFinCounterparties(await cpRes.json());
      if (baRes.ok) setFinBankAccounts(await baRes.json());
      if (bmRes.ok) setFinMovements(await bmRes.json());
    } catch {}
  };

  useEffect(() => {
    if (isAuthenticated && activeTab === 'finance') loadFinanceData();
  }, [isAuthenticated, activeTab]);

  // Load invoices
  const loadInvoices = async () => {
    try {
      const res = await authFetch('/api/invoices');
      if (res.ok) setInvoices(await res.json());
    } catch {}
  };
  useEffect(() => { if (isAuthenticated && activeTab === 'invoices') loadInvoices(); }, [isAuthenticated, activeTab]);

  // Load inventory
  const loadInventory = async () => {
    try {
      const res = await authFetch('/api/inventory');
      if (res.ok) setInventoryItems(await res.json());
    } catch {}
  };
  useEffect(() => { if (isAuthenticated && activeTab === 'inventory') loadInventory(); }, [isAuthenticated, activeTab]);

  // Load schedule
  const loadSchedule = async () => {
    try {
      const y = calendarMonth.getFullYear();
      const m = calendarMonth.getMonth();
      const from = `${y}-${String(m + 1).padStart(2, '0')}-01`;
      const lastDay = new Date(y, m + 1, 0).getDate();
      const to = `${y}-${String(m + 1).padStart(2, '0')}-${lastDay}`;
      const res = await authFetch(`/api/schedule?from=${from}&to=${to}`);
      if (res.ok) setScheduleEvents(await res.json());
    } catch {}
  };
  useEffect(() => { if (isAuthenticated && activeTab === 'calendar') loadSchedule(); }, [isAuthenticated, activeTab, calendarMonth]);

  // Load reports
  const loadReports = async () => {
    try {
      const res = await authFetch('/api/reports');
      if (res.ok) setReportsData(await res.json());
    } catch {}
  };
  useEffect(() => { if (isAuthenticated && activeTab === 'reports') loadReports(); }, [isAuthenticated, activeTab]);

  const handleLogin = async (username, password) => {
    setAuthError('');
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('mobel_auth_token', data.token);
        setUserRole(data.role || 'admin');
        setIsAuthenticated(true);
      } else {
        setAuthError(data.error || 'Invalid credentials');
      }
    } catch {
      setAuthError('Connection error. Please try again.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('mobel_auth_token');
    setIsAuthenticated(false);
    setUserRole(null);
  };

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const addOrUpdateCustomer = async (customer) => {
    try {
      if (editingItem && editingItem.id) {
        const res = await authFetch(`/api/customers/${editingItem.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(customer),
        });
        const updated = await res.json();
        setCustomers(customers.map(c => c.id === editingItem.id ? updated : c));
      } else {
        const res = await authFetch('/api/customers', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(customer),
        });
        const created = await res.json();
        setCustomers([...customers, created]);
      }
      closeModal();
    } catch (err) {
      console.error('Error saving customer:', err);
      alert('Failed to save customer. Please try again.');
    }
  };

  const deleteCustomer = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await authFetch(`/api/customers/${id}`, { method: 'DELETE' });
        setCustomers(customers.filter(c => c.id !== id));
      } catch (err) {
        console.error('Error deleting customer:', err);
        alert('Failed to delete customer. Please try again.');
      }
    }
  };

  const addOrUpdateEmployee = async (employee) => {
    try {
      if (editingItem && editingItem.id) {
        const res = await authFetch(`/api/employees/${editingItem.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(employee),
        });
        const updated = await res.json();
        setEmployees(employees.map(e => e.id === editingItem.id ? updated : e));
      } else {
        const res = await authFetch('/api/employees', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(employee),
        });
        const created = await res.json();
        setEmployees([...employees, created]);
      }
      closeModal();
    } catch (err) {
      console.error('Error saving employee:', err);
      alert('Failed to save employee. Please try again.');
    }
  };

  const deleteEmployee = async (id) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await authFetch(`/api/employees/${id}`, { method: 'DELETE' });
        setEmployees(employees.filter(e => e.id !== id));
      } catch (err) {
        console.error('Error deleting employee:', err);
        alert('Failed to delete employee. Please try again.');
      }
    }
  };

  const addOrUpdateEstimate = async (estimate) => {
    try {
      if (editingItem && editingItem.id) {
        const res = await authFetch(`/api/estimates/${editingItem.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(estimate),
        });
        const updated = await res.json();
        setEstimates(estimates.map(e => e.id === editingItem.id ? updated : e));
      } else {
        const res = await authFetch('/api/estimates', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(estimate),
        });
        const created = await res.json();
        setEstimates([...estimates, created]);
      }
      closeModal();
    } catch (err) {
      console.error('Error saving estimate:', err);
      alert('Failed to save estimate. Please try again.');
    }
  };

  const deleteEstimate = async (id) => {
    if (window.confirm('Are you sure you want to delete this estimate?')) {
      try {
        await authFetch(`/api/estimates/${id}`, { method: 'DELETE' });
        setEstimates(estimates.filter(e => e.id !== id));
      } catch (err) {
        console.error('Error deleting estimate:', err);
        alert('Failed to delete estimate. Please try again.');
      }
    }
  };

  const convertToProject = async (estimate) => {
    try {
      const newProject = {
        estimateId: estimate.id,
        customerId: estimate.customerId,
        customerName: estimate.customerName,
        description: estimate.description,
        totalCost: estimate.totalCost,
        status: 'In Progress',
        startDate: new Date().toISOString(),
        estimatedCompletion: estimate.installationDate,
        items: estimate.items,
      };
      const projectRes = await authFetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newProject),
      });
      const createdProject = await projectRes.json();
      setProjects([...projects, createdProject]);

      const estimateRes = await authFetch(`/api/estimates/${estimate.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...estimate, converted: true }),
      });
      const updatedEstimate = await estimateRes.json();
      setEstimates(estimates.map(e => e.id === estimate.id ? updatedEstimate : e));
    } catch (err) {
      console.error('Error converting estimate:', err);
      alert('Failed to convert estimate to project. Please try again.');
    }
  };

  const updateProjectStatus = async (id, status) => {
    try {
      const project = projects.find(p => p.id === id);
      const updatedData = {
        ...project,
        status,
        ...(status === 'Completed' && { completedDate: new Date().toISOString() }),
      };
      const res = await authFetch(`/api/projects/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedData),
      });
      const updated = await res.json();
      setProjects(projects.map(p => p.id === id ? updated : p));
    } catch (err) {
      console.error('Error updating project status:', err);
      alert('Failed to update project status. Please try again.');
    }
  };

  const deleteProject = async (id) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await authFetch(`/api/projects/${id}`, { method: 'DELETE' });
        setProjects(projects.filter(p => p.id !== id));
      } catch (err) {
        console.error('Error deleting project:', err);
        alert('Failed to delete project. Please try again.');
      }
    }
  };

  const addOrUpdateUser = async (userData) => {
    try {
      if (editingItem && editingItem.id) {
        const res = await authFetch(`/api/users/${editingItem.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(userData),
        });
        if (!res.ok) {
          const err = await res.json();
          alert(err.error || 'Failed to update user.');
          return;
        }
        const updated = await res.json();
        setUsers(users.map(u => u.id === editingItem.id ? updated : u));
      } else {
        const res = await authFetch('/api/users', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(userData),
        });
        if (!res.ok) {
          const err = await res.json();
          alert(err.error || 'Failed to create user.');
          return;
        }
        const created = await res.json();
        setUsers([...users, created]);
      }
      closeModal();
    } catch (err) {
      console.error('Error saving user:', err);
      alert('Failed to save user. Please try again.');
    }
  };

  const deleteUser = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await authFetch(`/api/users/${id}`, { method: 'DELETE' });
        setUsers(users.filter(u => u.id !== id));
      } catch (err) {
        console.error('Error deleting user:', err);
        alert('Failed to delete user. Please try again.');
      }
    }
  };

  // Finance CRUD helpers
  const addFinTransaction = async (tx) => {
    try {
      const res = await authFetch('/api/finance/transactions', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tx),
      });
      if (res.ok) { await loadFinanceData(); closeModal(); }
      else { const e = await res.json(); alert(e.error); }
    } catch { alert('Failed to save transaction.'); }
  };

  const updateFinTransaction = async (tx) => {
    try {
      const res = await authFetch(`/api/finance/transactions/${editingItem.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tx),
      });
      if (res.ok) { await loadFinanceData(); closeModal(); }
      else { const e = await res.json(); alert(e.error); }
    } catch { alert('Failed to update transaction.'); }
  };

  const deleteFinTransaction = async (id) => {
    if (window.confirm('Delete this transaction?')) {
      await authFetch(`/api/finance/transactions/${id}`, { method: 'DELETE' });
      await loadFinanceData();
    }
  };

  const settleFinTransaction = async (id) => {
    const tx = finTransactions.find(t => t.id === id);
    if (!tx) return;
    await authFetch(`/api/finance/transactions/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...tx, status: 'settled', paidAmount: tx.amount, settledAt: new Date().toISOString() }),
    });
    await loadFinanceData();
  };

  const addFinMovement = async (mv) => {
    try {
      const res = await authFetch('/api/finance/bank-movements', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mv),
      });
      if (res.ok) { await loadFinanceData(); closeModal(); }
    } catch { alert('Failed to save movement.'); }
  };

  const deleteFinMovement = async (id) => {
    if (window.confirm('Delete this movement?')) {
      await authFetch(`/api/finance/bank-movements/${id}`, { method: 'DELETE' });
      await loadFinanceData();
    }
  };

  const addFinCategory = async (cat) => {
    try {
      const res = await authFetch('/api/finance/categories', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cat),
      });
      if (res.ok) { await loadFinanceData(); closeModal(); }
    } catch { alert('Failed to save category.'); }
  };

  const addFinCounterparty = async (cp) => {
    try {
      const res = await authFetch('/api/finance/counterparties', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cp),
      });
      if (res.ok) { await loadFinanceData(); closeModal(); }
    } catch { alert('Failed to save counterparty.'); }
  };

  const addFinBankAccount = async (ba) => {
    try {
      const res = await authFetch('/api/finance/bank-accounts', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ba),
      });
      if (res.ok) { await loadFinanceData(); closeModal(); }
    } catch { alert('Failed to save bank account.'); }
  };

  // Invoice CRUD
  const addOrUpdateInvoice = async (inv) => {
    try {
      if (editingItem && editingItem.id) {
        const res = await authFetch(`/api/invoices/${editingItem.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(inv),
        });
        if (res.ok) { await loadInvoices(); closeModal(); } else { const e = await res.json(); alert(e.error); }
      } else {
        const res = await authFetch('/api/invoices', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(inv),
        });
        if (res.ok) { await loadInvoices(); closeModal(); } else { const e = await res.json(); alert(e.error); }
      }
    } catch { alert('Failed to save invoice.'); }
  };
  const deleteInvoice = async (id) => {
    if (window.confirm('Delete this invoice?')) { await authFetch(`/api/invoices/${id}`, { method: 'DELETE' }); await loadInvoices(); }
  };
  const recordPayment = async (invoice, amount) => {
    const newPaid = Number(invoice.paidAmount) + Number(amount);
    const payments = [...(invoice.payments || []), { amount: Number(amount), date: new Date().toISOString().slice(0, 10) }];
    let status = 'sent';
    if (newPaid >= invoice.total) status = 'paid';
    else if (newPaid > 0) status = 'partial';
    await authFetch(`/api/invoices/${invoice.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...invoice, paidAmount: newPaid, payments, status }),
    });
    await loadInvoices();
  };

  // Inventory CRUD
  const addOrUpdateInventoryItem = async (item) => {
    try {
      if (editingItem && editingItem.id) {
        const res = await authFetch(`/api/inventory/${editingItem.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(item),
        });
        if (res.ok) { await loadInventory(); closeModal(); }
      } else {
        const res = await authFetch('/api/inventory', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(item),
        });
        if (res.ok) { await loadInventory(); closeModal(); }
      }
    } catch { alert('Failed to save item.'); }
  };
  const deleteInventoryItem = async (id) => {
    if (window.confirm('Delete this item?')) { await authFetch(`/api/inventory/${id}`, { method: 'DELETE' }); await loadInventory(); }
  };

  // Schedule CRUD
  const addOrUpdateEvent = async (ev) => {
    try {
      if (editingItem && editingItem.id) {
        const res = await authFetch(`/api/schedule/${editingItem.id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(ev),
        });
        if (res.ok) { await loadSchedule(); closeModal(); }
      } else {
        const res = await authFetch('/api/schedule', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(ev),
        });
        if (res.ok) { await loadSchedule(); closeModal(); }
      }
    } catch { alert('Failed to save event.'); }
  };
  const deleteEvent = async (id) => {
    if (window.confirm('Delete this event?')) { await authFetch(`/api/schedule/${id}`, { method: 'DELETE' }); await loadSchedule(); }
  };

  const filteredCustomers = customers.filter(c => {
    const matchesFilter = customerFilter === 'all' || (c.customerType || 'p2p') === customerFilter;
    const term = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm ||
      c.name?.toLowerCase().includes(term) ||
      c.lastName?.toLowerCase().includes(term) ||
      c.companyName?.toLowerCase().includes(term) ||
      c.email?.toLowerCase().includes(term) ||
      c.phone?.includes(searchTerm);
    return matchesFilter && matchesSearch;
  });

  const filteredEstimates = estimates.filter(e =>
    e.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredProjects = projects.filter(p =>
    p.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalRevenue = projects.filter(p => p.status === 'Completed').reduce((sum, p) => sum + Number(p.totalCost || 0), 0);
  const inProgressRevenue = projects.filter(p => p.status === 'In Progress').reduce((sum, p) => sum + Number(p.totalCost || 0), 0);
  const pendingEstimates = estimates.filter(e => !e.converted).length;

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Verifying session...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} error={authError} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-gray-900 border-r border-gray-800">
        <div className="p-6">
          <div className="mb-8">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent" style={{ fontFamily: 'system-ui, -apple-system, sans-serif', fontWeight: 700 }}>
              Möbel Interiors
            </h1>
            <p className="text-xs text-gray-500 mt-1">mobelinteriors.com</p>
          </div>

          <nav className="space-y-2">
            <button
              onClick={() => { setActiveTab('dashboard'); setSearchTerm(''); }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === 'dashboard'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
              }`}
            >
              <Home size={20} />
              <span className="font-medium">Dashboard</span>
            </button>
            <button
              onClick={() => { setActiveTab('customers'); setSearchTerm(''); }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === 'customers'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
              }`}
            >
              <Users size={20} />
              <span className="font-medium">Customers</span>
              {customers.length > 0 && (
                <span className="ml-auto bg-gray-800 text-gray-300 px-2 py-0.5 rounded-full text-xs">
                  {customers.length}
                </span>
              )}
            </button>
            <button
              onClick={() => { setActiveTab('estimates'); setSearchTerm(''); }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === 'estimates'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
              }`}
            >
              <FileText size={20} />
              <span className="font-medium">Estimates</span>
              {estimates.length > 0 && (
                <span className="ml-auto bg-gray-800 text-gray-300 px-2 py-0.5 rounded-full text-xs">
                  {estimates.length}
                </span>
              )}
            </button>
            <button
              onClick={() => { setActiveTab('projects'); setSearchTerm(''); }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === 'projects'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
              }`}
            >
              <Briefcase size={20} />
              <span className="font-medium">Projects</span>
              {projects.length > 0 && (
                <span className="ml-auto bg-gray-800 text-gray-300 px-2 py-0.5 rounded-full text-xs">
                  {projects.length}
                </span>
              )}
            </button>
            <button
              onClick={() => { setActiveTab('employees'); setSearchTerm(''); }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === 'employees'
                  ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
              }`}
            >
              <Package size={20} />
              <span className="font-medium">Employees</span>
              {employees.length > 0 && (
                <span className="ml-auto bg-gray-800 text-gray-300 px-2 py-0.5 rounded-full text-xs">
                  {employees.length}
                </span>
              )}
            </button>
            <button
              onClick={() => { setActiveTab('invoices'); setSearchTerm(''); }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === 'invoices' ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20' : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
              }`}
            >
              <CreditCard size={20} />
              <span className="font-medium">Invoices</span>
            </button>
            <button
              onClick={() => { setActiveTab('inventory'); setSearchTerm(''); }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === 'inventory' ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20' : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
              }`}
            >
              <Warehouse size={20} />
              <span className="font-medium">Inventory</span>
              {inventoryItems.filter(i => i.quantity <= i.minQuantity && i.minQuantity > 0).length > 0 && (
                <span className="ml-auto bg-red-500/30 text-red-400 px-2 py-0.5 rounded-full text-xs">
                  {inventoryItems.filter(i => i.quantity <= i.minQuantity && i.minQuantity > 0).length}
                </span>
              )}
            </button>
            <button
              onClick={() => { setActiveTab('calendar'); setSearchTerm(''); }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === 'calendar' ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20' : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
              }`}
            >
              <Calendar size={20} />
              <span className="font-medium">Calendar</span>
            </button>
            <button
              onClick={() => { setActiveTab('reports'); setSearchTerm(''); }}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === 'reports' ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20' : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
              }`}
            >
              <BarChart3 size={20} />
              <span className="font-medium">Reports</span>
            </button>
            {userRole === 'admin' && (
              <button
                onClick={() => { setActiveTab('finance'); setSearchTerm(''); }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                  activeTab === 'finance'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                }`}
              >
                <DollarSign size={20} />
                <span className="font-medium">Finance</span>
              </button>
            )}
          </nav>
          <div className="absolute bottom-6 left-6 right-6 space-y-2">
            {userRole === 'admin' && (
              <button
                onClick={() => { setActiveTab('users'); setSearchTerm(''); }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                  activeTab === 'users'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/20'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                }`}
              >
                <Shield size={20} />
                <span className="font-medium">Users</span>
                {users.length > 0 && (
                  <span className="ml-auto bg-gray-800 text-gray-300 px-2 py-0.5 rounded-full text-xs">
                    {users.length}
                  </span>
                )}
              </button>
            )}
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-gray-400 hover:bg-gray-800 hover:text-gray-200 transition-all"
            >
              <LogOut size={20} />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 min-h-screen">
        <div className="p-8">
          {/* Header */}
          {!['dashboard','finance','invoices','inventory','calendar','reports'].includes(activeTab) && (
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                  {activeTab === 'customers' && 'Customers'}
                  {activeTab === 'estimates' && 'Estimates'}
                  {activeTab === 'projects' && 'Projects'}
                  {activeTab === 'employees' && 'Employees'}
                  {activeTab === 'users' && 'Users'}
                  {activeTab === 'invoices' && 'Invoices'}
                  {activeTab === 'inventory' && 'Inventory'}
                </h2>
                <p className="text-gray-500">
                  Manage {activeTab === 'customers' && 'your customers'}
                  {activeTab === 'estimates' && 'your estimates'}
                  {activeTab === 'projects' && 'your projects'}
                  {activeTab === 'employees' && 'your team'}
                  {activeTab === 'users' && 'dashboard users'}
                </p>
              </div>
              <button
                onClick={() => openModal(activeTab)}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-3 rounded-xl hover:shadow-lg hover:shadow-cyan-500/30 transition-all flex items-center space-x-2 font-medium"
              >
                <Plus size={20} />
                <span>Add New</span>
              </button>
            </div>
          )}

          {!['dashboard','finance','invoices','inventory','calendar','reports'].includes(activeTab) && (
            <div className="mb-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
                <input
                  type="text"
                  placeholder={`Search ${activeTab}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-gray-900 border border-gray-800 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
            </div>
          )}

          {/* Dashboard */}
          {activeTab === 'dashboard' && (
            <Dashboard
              totalRevenue={totalRevenue}
              inProgressRevenue={inProgressRevenue}
              pendingEstimates={pendingEstimates}
              recentProjects={projects.slice(0, 5)}
              recentEstimates={estimates.slice(0, 5)}
            />
          )}

          {/* Content Based on Active Tab */}
          {activeTab === 'customers' && (
            <CustomersList
              customers={filteredCustomers}
              onEdit={(customer) => openModal('customers', customer)}
              onDelete={deleteCustomer}
              customerFilter={customerFilter}
              setCustomerFilter={setCustomerFilter}
            />
          )}
          
          {activeTab === 'employees' && (
            <EmployeesList 
              employees={employees} 
              onEdit={(employee) => openModal('employees', employee)}
              onDelete={deleteEmployee}
            />
          )}
          
          {activeTab === 'estimates' && (
            <EstimatesList 
              estimates={filteredEstimates} 
              onEdit={(estimate) => openModal('estimates', estimate)}
              onDelete={deleteEstimate}
              onConvert={convertToProject}
            />
          )}
          
          {activeTab === 'projects' && (
            <ProjectsList
              projects={filteredProjects}
              onUpdateStatus={updateProjectStatus}
              onDelete={deleteProject}
            />
          )}

          {activeTab === 'users' && userRole === 'admin' && (
            <UsersList
              users={users}
              onEdit={(user) => openModal('users', user)}
              onDelete={deleteUser}
            />
          )}

          {activeTab === 'finance' && userRole === 'admin' && (
            <FinanceModule
              financeTab={financeTab}
              setFinanceTab={setFinanceTab}
              summary={finSummary}
              transactions={finTransactions}
              categories={finCategories}
              counterparties={finCounterparties}
              bankAccounts={finBankAccounts}
              movements={finMovements}
              onAddTransaction={() => openModal('fin-transaction')}
              onEditTransaction={(tx) => openModal('fin-transaction', tx)}
              onDeleteTransaction={deleteFinTransaction}
              onSettleTransaction={settleFinTransaction}
              onAddMovement={() => openModal('fin-movement')}
              onDeleteMovement={deleteFinMovement}
              onAddCategory={() => openModal('fin-category')}
              onAddCounterparty={() => openModal('fin-counterparty')}
              onAddBankAccount={() => openModal('fin-bank-account')}
            />
          )}

          {activeTab === 'invoices' && (
            <InvoicesModule
              invoices={invoices}
              customers={customers}
              onAdd={() => openModal('invoices')}
              onEdit={(inv) => openModal('invoices', inv)}
              onDelete={deleteInvoice}
              onRecordPayment={recordPayment}
            />
          )}

          {activeTab === 'inventory' && (
            <InventoryModule
              items={inventoryItems}
              onAdd={() => openModal('inventory')}
              onEdit={(item) => openModal('inventory', item)}
              onDelete={deleteInventoryItem}
            />
          )}

          {activeTab === 'calendar' && (
            <CalendarModule
              events={scheduleEvents}
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
              onAddEvent={() => openModal('schedule')}
              onEditEvent={(ev) => openModal('schedule', ev)}
              onDeleteEvent={deleteEvent}
              customers={customers}
              employees={employees}
            />
          )}

          {activeTab === 'reports' && (
            <ReportsModule data={reportsData} />
          )}
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <Modal
          type={modalType}
          item={editingItem}
          onClose={closeModal}
          onSave={
            modalType === 'customers' ? addOrUpdateCustomer :
            modalType === 'employees' ? addOrUpdateEmployee :
            modalType === 'estimates' ? addOrUpdateEstimate :
            modalType === 'users' ? addOrUpdateUser :
            modalType === 'fin-transaction' ? (editingItem ? updateFinTransaction : addFinTransaction) :
            modalType === 'fin-movement' ? addFinMovement :
            modalType === 'fin-category' ? addFinCategory :
            modalType === 'fin-counterparty' ? addFinCounterparty :
            modalType === 'fin-bank-account' ? addFinBankAccount :
            modalType === 'invoices' ? addOrUpdateInvoice :
            modalType === 'inventory' ? addOrUpdateInventoryItem :
            modalType === 'schedule' ? addOrUpdateEvent : null
          }
          customers={customers}
          employees={employees}
          projects={projects}
          finCategories={finCategories}
          finCounterparties={finCounterparties}
          finBankAccounts={finBankAccounts}
        />
      )}
    </div>
  );
};

// Login Screen Component
const LoginScreen = ({ onLogin, error }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await onLogin(username, password);
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <div className="w-full max-w-md px-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
            Mobel Interiors
          </h1>
          <p className="text-gray-500 mt-2">Sign in to your dashboard</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
              {error}
            </div>
          )}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">Username</label>
            <input
              type="text"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none"
              placeholder="Enter username"
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none"
              placeholder="Enter password"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-3 rounded-xl hover:shadow-lg hover:shadow-cyan-500/30 transition-all font-medium disabled:opacity-50"
          >
            {submitting ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Dashboard Component
const Dashboard = ({ totalRevenue, inProgressRevenue, pendingEstimates, recentProjects, recentEstimates }) => {
  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
          Dashboard
        </h2>
        <p className="text-gray-500">Overview of your business</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-green-500/20 p-3 rounded-xl">
              <DollarSign className="text-green-400" size={24} />
            </div>
            <span className="text-xs text-green-400 font-medium">Completed</span>
          </div>
          <h3 className="text-gray-400 text-sm mb-2">Total Revenue</h3>
          <p className="text-3xl font-bold text-green-400">${fmtMoney(totalRevenue)}</p>
        </div>

        <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-cyan-500/20 p-3 rounded-xl">
              <TrendingUp className="text-cyan-400" size={24} />
            </div>
            <span className="text-xs text-cyan-400 font-medium">In Progress</span>
          </div>
          <h3 className="text-gray-400 text-sm mb-2">In Progress</h3>
          <p className="text-3xl font-bold text-cyan-400">${fmtMoney(inProgressRevenue)}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-purple-500/20 p-3 rounded-xl">
              <FileText className="text-purple-400" size={24} />
            </div>
            <span className="text-xs text-purple-400 font-medium">Pending</span>
          </div>
          <h3 className="text-gray-400 text-sm mb-2">Open Estimates</h3>
          <p className="text-3xl font-bold text-purple-400">{pendingEstimates}</p>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
          <h3 className="text-xl font-bold mb-4">Recent Projects</h3>
          {recentProjects.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No projects yet</p>
          ) : (
            <div className="space-y-3">
              {recentProjects.map(project => (
                <div key={project.id} className="flex items-center justify-between p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                  <div className="flex-1">
                    <p className="font-medium mb-1">{project.customerName}</p>
                    <p className="text-sm text-gray-500">{project.description}</p>
                  </div>
                  <StatusBadge status={project.status} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
          <h3 className="text-xl font-bold mb-4">Recent Estimates</h3>
          {recentEstimates.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No estimates yet</p>
          ) : (
            <div className="space-y-3">
              {recentEstimates.map(estimate => (
                <div key={estimate.id} className="flex items-center justify-between p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                  <div className="flex-1">
                    <p className="font-medium mb-1">{estimate.customerName}</p>
                    <p className="text-sm text-gray-500">{estimate.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-cyan-400">${fmtMoney(estimate.totalCost)}</p>
                    {estimate.converted && (
                      <span className="text-xs text-green-400">Converted</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    'In Progress': { bg: 'bg-cyan-500/20', text: 'text-cyan-400', border: 'border-cyan-500/30' },
    'Received Total': { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
    'Received Partial': { bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
    'Open': { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
    'Completed': { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
    'On Hold': { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
    'Cancelled': { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
    'New Estimate': { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' }
  };

  const config = statusConfig[status] || statusConfig['In Progress'];

  return (
    <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
      {status}
    </span>
  );
};

// Customers List Component
const CustomersList = ({ customers, onEdit, onDelete, customerFilter, setCustomerFilter }) => {
  const filters = [
    { key: 'all', label: 'All' },
    { key: 'business', label: 'Business' },
    { key: 'p2p', label: 'Personal' },
  ];

  return (
    <div>
      <div className="flex space-x-1 bg-gray-900 rounded-xl p-1 mb-6 border border-gray-800 max-w-md">
        {filters.map(f => (
          <button key={f.key} onClick={() => setCustomerFilter(f.key)}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
              customerFilter === f.key
                ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
            }`}
          >{f.label}</button>
        ))}
      </div>

      {customers.length === 0 ? (
        <div className="text-center py-16 bg-gray-900 rounded-2xl border border-gray-800">
          <Users className="mx-auto text-gray-700 mb-4" size={48} />
          <p className="text-gray-400 text-lg">No customers found</p>
          <p className="text-gray-600 text-sm mt-2">{customerFilter !== 'all' ? 'Try a different filter' : 'Add your first customer to get started'}</p>
        </div>
      ) : (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-800/50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Name</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Phone</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Email</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Address</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {customers.map(customer => (
                <tr key={customer.id} className="hover:bg-gray-800/30 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium text-gray-100">
                        {customer.customerType === 'business' ? customer.companyName || customer.name : `${customer.name} ${customer.lastName || ''}`.trim()}
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${customer.customerType === 'business' ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-700/50 text-gray-500'}`}>
                        {customer.customerType === 'business' ? 'BIZ' : 'P2P'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-400">
                      {customer.customerType === 'business' ? customer.contactPerson || '—' : '—'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-400">{formatPhoneDisplay(customer.phone) || '—'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-400 max-w-[200px] truncate">{customer.email || '—'}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-400 max-w-[200px] truncate">{customer.address || '—'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button onClick={() => onEdit(customer)} className="text-cyan-400 hover:text-cyan-300 mr-3">
                      <Edit2 size={18} />
                    </button>
                    <button onClick={() => onDelete(customer.id)} className="text-red-400 hover:text-red-300">
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Employees List Component
const EmployeesList = ({ employees, onEdit, onDelete }) => {
  if (employees.length === 0) {
    return (
      <div className="text-center py-16 bg-gray-900 rounded-2xl border border-gray-800">
        <Package className="mx-auto text-gray-700 mb-4" size={48} />
        <p className="text-gray-400 text-lg">No employees yet</p>
        <p className="text-gray-600 text-sm mt-2">Add your first employee to get started</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
      <table className="min-w-full">
        <thead className="bg-gray-800/50">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Name</th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Role</th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Phone</th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Hourly Rate</th>
            <th className="px-6 py-4 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800">
          {employees.map(employee => (
            <tr key={employee.id} className="hover:bg-gray-800/30 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-100">{employee.name}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-400">{employee.role}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-400">{formatPhoneDisplay(employee.phone)}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-cyan-400 font-medium">${employee.hourlyRate}/hr</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <button onClick={() => onEdit(employee)} className="text-cyan-400 hover:text-cyan-300 mr-3">
                  <Edit2 size={18} />
                </button>
                <button onClick={() => onDelete(employee.id)} className="text-red-400 hover:text-red-300">
                  <Trash2 size={18} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Estimates List Component
const EstimatesList = ({ estimates, onEdit, onDelete, onConvert }) => {
  if (estimates.length === 0) {
    return (
      <div className="text-center py-16 bg-gray-900 rounded-2xl border border-gray-800">
        <FileText className="mx-auto text-gray-700 mb-4" size={48} />
        <p className="text-gray-400 text-lg">No estimates yet</p>
        <p className="text-gray-600 text-sm mt-2">Create your first estimate to get started</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {estimates.map(estimate => (
        <div key={estimate.id} className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-cyan-500/30 transition-all">
          <div className="flex justify-between items-start mb-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-semibold text-gray-100">{estimate.customerName}</h3>
                {estimate.converted && (
                  <StatusBadge status="Received Total" />
                )}
              </div>
              <p className="text-sm text-gray-500">{estimate.description}</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-cyan-400">${fmtMoney(estimate.totalCost)}</div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4 text-sm bg-gray-800/30 rounded-xl p-4">
            <div>
              <span className="text-gray-500 block mb-1">Installation Date</span>
              <span className="text-gray-300 font-medium">{new Date(estimate.installationDate).toLocaleDateString('en-US')}</span>
            </div>
            <div>
              <span className="text-gray-500 block mb-1">Labor Hours</span>
              <span className="text-gray-300 font-medium">{estimate.laborHours}h @ ${estimate.laborRate}/hr</span>
            </div>
          </div>

          {estimate.items && estimate.items.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-400 mb-3">Estimate Items:</h4>
              <div className="bg-gray-800/50 rounded-xl p-4 space-y-2">
                {estimate.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm border-b border-gray-800 pb-2 last:border-0 last:pb-0">
                    <span className="text-gray-300">
                      {item.name ? `• ${item.name}` : `${item.quantity || 1}x ${item.type || 'Item'}${item.dimensions ? ` - ${item.dimensions}` : ''}${item.material ? ` (${item.material}${item.finish ? `, ${item.finish}` : ''})` : ''}`}
                    </span>
                    {item.cost && <span className="font-medium text-cyan-400">${fmtMoney(item.cost)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4 border-t border-gray-800">
            <button 
              onClick={() => onEdit(estimate)} 
              className="px-4 py-2 text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-colors flex items-center space-x-1"
            >
              <Edit2 size={16} />
              <span>Edit</span>
            </button>
            {!estimate.converted && (
              <button 
                onClick={() => onConvert(estimate)} 
                className="px-4 py-2 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg transition-colors flex items-center space-x-1 border border-green-500/30"
              >
                <CheckCircle size={16} />
                <span>Convert to Project</span>
              </button>
            )}
            <button 
              onClick={() => onDelete(estimate.id)} 
              className="px-4 py-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex items-center space-x-1"
            >
              <Trash2 size={16} />
              <span>Delete</span>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Projects List Component
const ProjectsList = ({ projects, onUpdateStatus, onDelete }) => {
  if (projects.length === 0) {
    return (
      <div className="text-center py-16 bg-gray-900 rounded-2xl border border-gray-800">
        <Briefcase className="mx-auto text-gray-700 mb-4" size={48} />
        <p className="text-gray-400 text-lg">No active projects</p>
        <p className="text-gray-600 text-sm mt-2">Convert an estimate to start your first project</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {projects.map(project => (
        <div key={project.id} className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-cyan-500/30 transition-all">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="text-lg font-semibold text-gray-100">{project.customerName}</h3>
                <StatusBadge status={project.status} />
              </div>
              <p className="text-sm text-gray-500">{project.description}</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-cyan-400">${fmtMoney(project.totalCost)}</div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-4 text-sm bg-gray-800/30 rounded-xl p-4">
            <div>
              <span className="text-gray-500 block mb-1">Start Date</span>
              <span className="text-gray-300 font-medium">{new Date(project.startDate).toLocaleDateString('en-US')}</span>
            </div>
            <div>
              <span className="text-gray-500 block mb-1">Expected Completion</span>
              <span className="text-gray-300 font-medium">{new Date(project.estimatedCompletion).toLocaleDateString('en-US')}</span>
            </div>
            {project.completedDate && (
              <div>
                <span className="text-gray-500 block mb-1">Completed Date</span>
                <span className="text-green-400 font-medium">{new Date(project.completedDate).toLocaleDateString('en-US')}</span>
              </div>
            )}
          </div>

          {project.items && project.items.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-400 mb-3">Project Items:</h4>
              <div className="bg-gray-800/50 rounded-xl p-4">
                {project.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm text-gray-300 mb-2 last:mb-0">
                    <span>
                      {item.name ? `• ${item.name}` : `${item.quantity || 1}x ${item.type || 'Item'}${item.dimensions ? ` - ${item.dimensions}` : ''}${item.material ? ` (${item.material}${item.finish ? `, ${item.finish}` : ''})` : ''}`}
                    </span>
                    {item.cost && <span className="text-cyan-400">${fmtMoney(item.cost)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4 border-t border-gray-800">
            <select
              value={project.status}
              onChange={(e) => onUpdateStatus(project.id, e.target.value)}
              className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            >
              <option value="In Progress">In Progress</option>
              <option value="On Hold">On Hold</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
            <button
              onClick={() => onDelete(project.id)}
              className="px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-500/20 transition-all"
            >
              <Trash2 size={18} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Users List Component (Admin Only)
const UsersList = ({ users, onEdit, onDelete }) => {
  if (users.length === 0) {
    return (
      <div className="text-center py-16 bg-gray-900 rounded-2xl border border-gray-800">
        <Shield className="mx-auto text-gray-700 mb-4" size={48} />
        <p className="text-gray-400 text-lg">No users yet</p>
        <p className="text-gray-600 text-sm mt-2">Add your first user to get started</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
      <table className="min-w-full">
        <thead className="bg-gray-800/50">
          <tr>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Name</th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Username</th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Email</th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Phone</th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Role</th>
            <th className="px-6 py-4 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800">
          {users.map(user => (
            <tr key={user.id} className="hover:bg-gray-800/30 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-100">{user.firstName} {user.lastName || ''}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-400">{user.username}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-400">{user.email || '—'}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-400">{user.phone ? formatPhoneDisplay(user.phone) : '—'}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                  {user.role}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <button onClick={() => onEdit(user)} className="text-cyan-400 hover:text-cyan-300 mr-3">
                  <Edit2 size={18} />
                </button>
                <button onClick={() => onDelete(user.id)} className="text-red-400 hover:text-red-300">
                  <Trash2 size={18} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Finance Module Component
const FinanceModule = ({ financeTab, setFinanceTab, summary, transactions, categories, counterparties, bankAccounts, movements, onAddTransaction, onEditTransaction, onDeleteTransaction, onSettleTransaction, onAddMovement, onDeleteMovement, onAddCategory, onAddCounterparty, onAddBankAccount }) => {
  const [finSearch, setFinSearch] = useState('');
  const [finDateMode, setFinDateMode] = useState('all');
  const [finDateFrom, setFinDateFrom] = useState('');
  const [finDateTo, setFinDateTo] = useState('');
  const [finStatusFilter, setFinStatusFilter] = useState('all');

  const tabs = [
    { key: 'receivables', label: 'Accounts Receivable' },
    { key: 'payables', label: 'Accounts Payable' },
    { key: 'statement', label: 'General Statement' },
    { key: 'movements', label: 'Bank Movements' },
  ];

  const getDateRange = () => {
    const today = new Date();
    if (finDateMode === 'today') {
      const d = today.toISOString().split('T')[0];
      return { from: d, to: d };
    }
    if (finDateMode === 'month') {
      const y = today.getFullYear();
      const m = String(today.getMonth() + 1).padStart(2, '0');
      return { from: `${y}-${m}-01`, to: `${y}-${m}-31` };
    }
    if (finDateMode === 'custom') {
      return { from: finDateFrom, to: finDateTo };
    }
    return { from: '', to: '' };
  };

  const filterTx = (items) => {
    const { from, to } = getDateRange();
    return items.filter(tx => {
      const term = finSearch.toLowerCase();
      const matchesSearch = !finSearch ||
        tx.description?.toLowerCase().includes(term) ||
        tx.counterpartyName?.toLowerCase().includes(term) ||
        tx.categoryName?.toLowerCase().includes(term) ||
        tx.notes?.toLowerCase().includes(term);
      const matchesDate = (!from || tx.date >= from) && (!to || tx.date <= to);
      const matchesStatus = finStatusFilter === 'all' || tx.status === finStatusFilter;
      return matchesSearch && matchesDate && matchesStatus;
    });
  };

  const receivables = transactions.filter(t => t.direction === 'in');
  const payables = transactions.filter(t => t.direction === 'out');

  const statusColor = (s) => {
    const m = { settled: 'text-green-400 bg-green-500/20 border-green-500/30', paid: 'text-green-400 bg-green-500/20 border-green-500/30', pending: 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30', partial: 'text-blue-400 bg-blue-500/20 border-blue-500/30', overdue: 'text-red-400 bg-red-500/20 border-red-500/30', canceled: 'text-gray-400 bg-gray-500/20 border-gray-500/30' };
    return m[s] || m.pending;
  };

  const dateModes = [
    { key: 'all', label: 'All' },
    { key: 'today', label: 'Today' },
    { key: 'month', label: 'This Month' },
    { key: 'custom', label: 'Custom' },
  ];

  const statusOptions = ['all', 'pending', 'paid', 'settled', 'partial', 'overdue', 'canceled'];

  const FilterBar = () => (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-4 space-y-3">
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-[200px]">
          <input type="text" placeholder="Search description, counterparty..." value={finSearch} onChange={(e) => setFinSearch(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
        </div>
        <div className="flex space-x-1 bg-gray-800 rounded-lg p-0.5">
          {dateModes.map(dm => (
            <button key={dm.key} onClick={() => setFinDateMode(dm.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${finDateMode === dm.key ? 'bg-cyan-600 text-white' : 'text-gray-400 hover:text-gray-200'}`}
            >{dm.label}</button>
          ))}
        </div>
        <select value={finStatusFilter} onChange={(e) => setFinStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent capitalize">
          {statusOptions.map(s => <option key={s} value={s} className="capitalize">{s === 'all' ? 'All Status' : s}</option>)}
        </select>
      </div>
      {finDateMode === 'custom' && (
        <div className="flex gap-3 items-center">
          <label className="text-xs text-gray-400">From:</label>
          <input type="date" value={finDateFrom} onChange={(e) => setFinDateFrom(e.target.value)}
            className="px-3 py-1.5 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
          <label className="text-xs text-gray-400">To:</label>
          <input type="date" value={finDateTo} onChange={(e) => setFinDateTo(e.target.value)}
            className="px-3 py-1.5 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
        </div>
      )}
    </div>
  );

  const TxTable = ({ items, title }) => {
    const filtered = filterTx(items);
    return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">{title} ({filtered.length}{filtered.length !== items.length ? ` of ${items.length}` : ''})</h3>
        <button onClick={onAddTransaction} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-2 rounded-xl hover:shadow-lg hover:shadow-cyan-500/30 transition-all flex items-center space-x-2 text-sm font-medium">
          <Plus size={16} /><span>Add Transaction</span>
        </button>
      </div>
      <FilterBar />
      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-gray-900 rounded-2xl border border-gray-800">
          <DollarSign className="mx-auto text-gray-700 mb-3" size={40} />
          <p className="text-gray-400">{items.length === 0 ? 'No transactions yet' : 'No transactions match your filters'}</p>
        </div>
      ) : (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Counterparty</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Category</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {filtered.map(tx => (
                <tr key={tx.id} className="hover:bg-gray-800/30 transition-colors">
                  <td className="px-4 py-3 text-sm text-gray-300">{tx.date}</td>
                  <td className="px-4 py-3 text-sm text-gray-100">{tx.description || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">{tx.counterpartyName || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">{tx.categoryName || '—'}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-cyan-400">${fmtMoney(tx.amount)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${statusColor(tx.status)}`}>{tx.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end space-x-1">
                      {tx.status !== 'settled' && tx.status !== 'canceled' && (
                        <button onClick={() => onSettleTransaction(tx.id)} className="text-green-400 hover:text-green-300 p-1" title="Mark Settled">
                          <CheckCircle size={16} />
                        </button>
                      )}
                      <button onClick={() => onEditTransaction(tx)} className="text-cyan-400 hover:text-cyan-300 p-1">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => onDeleteTransaction(tx.id)} className="text-red-400 hover:text-red-300 p-1">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );};

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>Finance</h2>
        <p className="text-gray-500">Manage your finances</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-2xl p-5">
          <h3 className="text-gray-400 text-xs mb-1 uppercase">Total Receipts</h3>
          <p className="text-2xl font-bold text-green-400">${fmtMoney(summary.totalReceipts)}</p>
        </div>
        <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-2xl p-5">
          <h3 className="text-gray-400 text-xs mb-1 uppercase">Total Payments</h3>
          <p className="text-2xl font-bold text-red-400">${fmtMoney(summary.totalPayments)}</p>
        </div>
        <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-2xl p-5">
          <h3 className="text-gray-400 text-xs mb-1 uppercase">Net</h3>
          <p className={`text-2xl font-bold ${summary.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>${fmtMoney(summary.net)}</p>
        </div>
        <div className="bg-gradient-to-br from-orange-500/10 to-yellow-500/10 border border-orange-500/20 rounded-2xl p-5">
          <h3 className="text-gray-400 text-xs mb-1 uppercase">Overdue</h3>
          <p className="text-2xl font-bold text-orange-400">{summary.overdueCount} <span className="text-sm font-normal">(${fmtMoney(summary.overdueAmount)})</span></p>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex space-x-1 bg-gray-900 rounded-xl p-1 mb-6 border border-gray-800">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setFinanceTab(t.key)}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${financeTab === t.key ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'}`}
          >{t.label}</button>
        ))}
      </div>

      {/* Quick add buttons for settings */}
      <div className="flex space-x-2 mb-6">
        <button onClick={onAddBankAccount} className="px-3 py-1.5 text-xs bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors">+ Bank Account</button>
        <button onClick={onAddCategory} className="px-3 py-1.5 text-xs bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors">+ Category</button>
        <button onClick={onAddCounterparty} className="px-3 py-1.5 text-xs bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors">+ Counterparty</button>
      </div>

      {/* Content */}
      {financeTab === 'receivables' && <TxTable items={receivables} title="Accounts Receivable" />}
      {financeTab === 'payables' && <TxTable items={payables} title="Accounts Payable" />}
      {financeTab === 'statement' && <TxTable items={transactions} title="General Statement" />}
      {financeTab === 'movements' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Bank Movements ({movements.length})</h3>
            <button onClick={onAddMovement} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-2 rounded-xl hover:shadow-lg hover:shadow-cyan-500/30 transition-all flex items-center space-x-2 text-sm font-medium">
              <Plus size={16} /><span>Add Movement</span>
            </button>
          </div>
          {movements.length === 0 ? (
            <div className="text-center py-12 bg-gray-900 rounded-2xl border border-gray-800">
              <DollarSign className="mx-auto text-gray-700 mb-3" size={40} />
              <p className="text-gray-400">No bank movements yet</p>
            </div>
          ) : (
            <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
              <table className="min-w-full">
                <thead className="bg-gray-800/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Description</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase">Amount</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400 uppercase">Reconciled</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {movements.map(mv => (
                    <tr key={mv.id} className="hover:bg-gray-800/30 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-300">{mv.date}</td>
                      <td className="px-4 py-3 text-sm text-gray-100">{mv.description || '—'}</td>
                      <td className={`px-4 py-3 text-sm text-right font-medium ${mv.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>${fmtMoney(mv.amount)}</td>
                      <td className="px-4 py-3 text-center">
                        {mv.reconciled ? <CheckCircle size={16} className="inline text-green-400" /> : <span className="text-gray-600 text-xs">No</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => onDeleteMovement(mv.id)} className="text-red-400 hover:text-red-300 p-1">
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Invoices Module
const InvoicesModule = ({ invoices, customers, onAdd, onEdit, onDelete, onRecordPayment }) => {
  const [invSearch, setInvSearch] = useState('');
  const [invStatus, setInvStatus] = useState('all');
  const [invDateMode, setInvDateMode] = useState('all');
  const [invDateFrom, setInvDateFrom] = useState('');
  const [invDateTo, setInvDateTo] = useState('');

  const statusColor = (s) => {
    const m = { draft:'text-gray-400 bg-gray-500/20 border-gray-500/30', sent:'text-blue-400 bg-blue-500/20 border-blue-500/30', partial:'text-yellow-400 bg-yellow-500/20 border-yellow-500/30', paid:'text-green-400 bg-green-500/20 border-green-500/30', overdue:'text-red-400 bg-red-500/20 border-red-500/30' };
    return m[s] || m.draft;
  };

  const filtered = invoices.filter(inv => {
    const term = invSearch.toLowerCase();
    const matchesSearch = !invSearch ||
      inv.invoiceNumber?.toLowerCase().includes(term) ||
      inv.customerName?.toLowerCase().includes(term) ||
      inv.notes?.toLowerCase().includes(term);
    const matchesStatus = invStatus === 'all' || inv.status === invStatus;
    let matchesDate = true;
    if (invDateMode === 'today') {
      const today = new Date().toISOString().slice(0, 10);
      matchesDate = inv.issueDate === today;
    } else if (invDateMode === 'month') {
      const ym = new Date().toISOString().slice(0, 7);
      matchesDate = inv.issueDate?.startsWith(ym);
    } else if (invDateMode === 'custom') {
      if (invDateFrom) matchesDate = matchesDate && inv.issueDate >= invDateFrom;
      if (invDateTo) matchesDate = matchesDate && inv.issueDate <= invDateTo;
    }
    return matchesSearch && matchesStatus && matchesDate;
  });

  const totalFiltered = filtered.reduce((s, i) => s + Number(i.total || 0), 0);
  const paidFiltered = filtered.reduce((s, i) => s + Number(i.paidAmount || 0), 0);
  const outstandingFiltered = totalFiltered - paidFiltered;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div><h2 className="text-3xl font-bold mb-1">Invoices</h2><p className="text-gray-500">Billing & payments</p></div>
        <button onClick={onAdd} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-3 rounded-xl hover:shadow-lg hover:shadow-cyan-500/30 transition-all flex items-center space-x-2 font-medium"><Plus size={20} /><span>New Invoice</span></button>
      </div>

      {/* Filter Bar */}
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4 mb-6 space-y-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px]">
            <input type="text" placeholder="Search invoices, customers..." value={invSearch} onChange={e => setInvSearch(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-500" />
          </div>
          <select value={invStatus} onChange={e => setInvStatus(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-500">
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="partial">Partial</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
          </select>
          <div className="flex bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
            {['all','today','month','custom'].map(m => (
              <button key={m} onClick={() => setInvDateMode(m)}
                className={`px-3 py-2 text-xs font-medium transition-colors ${invDateMode === m ? 'bg-cyan-600 text-white' : 'text-gray-400 hover:text-gray-200'}`}>
                {m === 'all' ? 'All Dates' : m === 'today' ? 'Today' : m === 'month' ? 'This Month' : 'Custom'}
              </button>
            ))}
          </div>
        </div>
        {invDateMode === 'custom' && (
          <div className="flex gap-3 items-center">
            <input type="date" value={invDateFrom} onChange={e => setInvDateFrom(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-500" />
            <span className="text-gray-500 text-sm">to</span>
            <input type="date" value={invDateTo} onChange={e => setInvDateTo(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-cyan-500" />
          </div>
        )}
        {/* Summary stats */}
        <div className="flex gap-4 text-sm">
          <span className="text-gray-400">Showing <span className="text-white font-medium">{filtered.length}</span> of {invoices.length}</span>
          <span className="text-gray-600">|</span>
          <span className="text-gray-400">Total: <span className="text-cyan-400 font-medium">${fmtMoney(totalFiltered)}</span></span>
          <span className="text-gray-400">Paid: <span className="text-green-400 font-medium">${fmtMoney(paidFiltered)}</span></span>
          <span className="text-gray-400">Outstanding: <span className="text-yellow-400 font-medium">${fmtMoney(outstandingFiltered)}</span></span>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-gray-900 rounded-2xl border border-gray-800"><CreditCard className="mx-auto text-gray-700 mb-4" size={48} /><p className="text-gray-400 text-lg">{invoices.length === 0 ? 'No invoices yet' : 'No invoices match your filters'}</p></div>
      ) : (
        <div className="space-y-4">
          {filtered.map(inv => (
            <div key={inv.id} className="bg-gray-900 border border-gray-800 rounded-2xl p-6 hover:border-cyan-500/30 transition-all">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-lg font-semibold">{inv.invoiceNumber}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${statusColor(inv.status)}`}>{inv.status}</span>
                  </div>
                  <p className="text-sm text-gray-400">{inv.customerName}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-cyan-400">${fmtMoney(inv.total)}</p>
                  {Number(inv.paidAmount) > 0 && Number(inv.paidAmount) < Number(inv.total) && <p className="text-xs text-gray-400">Paid: ${fmtMoney(inv.paidAmount)}</p>}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm bg-gray-800/30 rounded-xl p-3 mb-3">
                <div><span className="text-gray-500 block">Issue Date</span><span className="text-gray-300">{inv.issueDate}</span></div>
                <div><span className="text-gray-500 block">Due Date</span><span className="text-gray-300">{inv.dueDate || '—'}</span></div>
                <div><span className="text-gray-500 block">Tax</span><span className="text-gray-300">{inv.taxRate}%</span></div>
              </div>
              <div className="flex justify-end space-x-2 pt-3 border-t border-gray-800">
                {inv.status !== 'paid' && (
                  <button onClick={() => { const a = prompt('Payment amount:'); if (a && Number(a) > 0) onRecordPayment(inv, a); }}
                    className="px-3 py-2 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg text-sm flex items-center space-x-1 border border-green-500/30">
                    <DollarSign size={14} /><span>Record Payment</span>
                  </button>
                )}
                <button onClick={() => onEdit(inv)} className="px-3 py-2 text-cyan-400 hover:bg-cyan-500/10 rounded-lg text-sm flex items-center space-x-1"><Edit2 size={14} /><span>Edit</span></button>
                <button onClick={() => onDelete(inv.id)} className="px-3 py-2 text-red-400 hover:bg-red-500/10 rounded-lg text-sm flex items-center space-x-1"><Trash2 size={14} /><span>Delete</span></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Inventory Module
const InventoryModule = ({ items, onAdd, onEdit, onDelete }) => {
  const [invSearch, setInvSearch] = useState('');
  const [invCategory, setInvCategory] = useState('all');
  const [invLowOnly, setInvLowOnly] = useState(false);

  const allCategories = [...new Set(items.map(i => i.category).filter(Boolean))].sort();
  const lowStock = items.filter(i => Number(i.quantity) <= Number(i.minQuantity) && Number(i.minQuantity) > 0);

  const filtered = items.filter(item => {
    const term = invSearch.toLowerCase();
    const matchesSearch = !invSearch ||
      item.name?.toLowerCase().includes(term) ||
      item.sku?.toLowerCase().includes(term) ||
      item.supplier?.toLowerCase().includes(term) ||
      item.location?.toLowerCase().includes(term);
    const matchesCat = invCategory === 'all' || item.category === invCategory;
    const matchesLow = !invLowOnly || (Number(item.quantity) <= Number(item.minQuantity) && Number(item.minQuantity) > 0);
    return matchesSearch && matchesCat && matchesLow;
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div><h2 className="text-3xl font-bold mb-1">Inventory</h2><p className="text-gray-500">Materials & supplies</p></div>
        <button onClick={onAdd} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-3 rounded-xl hover:shadow-lg hover:shadow-cyan-500/30 transition-all flex items-center space-x-2 font-medium"><Plus size={20} /><span>Add Item</span></button>
      </div>
      {lowStock.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 mb-6">
          <h3 className="text-red-400 font-semibold mb-2 flex items-center gap-2"><AlertCircle size={18} /> Low Stock Alerts ({lowStock.length})</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {lowStock.map(i => <div key={i.id} className="bg-gray-900/50 rounded-lg p-2 text-sm"><span className="text-gray-100 font-medium">{i.name}</span><br/><span className="text-red-400">{i.quantity} {i.unit}</span><span className="text-gray-500"> / min {i.minQuantity}</span></div>)}
          </div>
        </div>
      )}
      {/* Filters */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-[200px]">
          <input type="text" placeholder="Search name, SKU, supplier..." value={invSearch} onChange={(e) => setInvSearch(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
        </div>
        <select value={invCategory} onChange={(e) => setInvCategory(e.target.value)}
          className="px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
          <option value="all">All Categories</option>
          {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={() => setInvLowOnly(!invLowOnly)}
          className={`px-3 py-2 text-sm rounded-lg font-medium transition-all border ${invLowOnly ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-gray-800 text-gray-400 border-gray-700 hover:text-gray-200'}`}>
          Low Stock Only
        </button>
      </div>
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-gray-900 rounded-2xl border border-gray-800"><Warehouse className="mx-auto text-gray-700 mb-4" size={48} /><p className="text-gray-400 text-lg">{items.length === 0 ? 'No items yet' : 'No items match your filters'}</p></div>
      ) : (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Item</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">SKU</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Category</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase">Qty</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase">Cost/Unit</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Supplier</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {filtered.map(item => (
                <tr key={item.id} className={`hover:bg-gray-800/30 transition-colors ${Number(item.quantity) <= Number(item.minQuantity) && Number(item.minQuantity) > 0 ? 'bg-red-500/5' : ''}`}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-100">{item.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">{item.sku || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">{item.category || '—'}</td>
                  <td className={`px-4 py-3 text-sm text-right font-medium ${Number(item.quantity) <= Number(item.minQuantity) && Number(item.minQuantity) > 0 ? 'text-red-400' : 'text-gray-100'}`}>{item.quantity} {item.unit}</td>
                  <td className="px-4 py-3 text-sm text-right text-cyan-400">${fmtMoney(item.costPerUnit)}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">{item.supplier || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => onEdit(item)} className="text-cyan-400 hover:text-cyan-300 mr-2"><Edit2 size={16} /></button>
                    <button onClick={() => onDelete(item.id)} className="text-red-400 hover:text-red-300"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Calendar Module
const CalendarModule = ({ events, month, onMonthChange, onAddEvent, onEditEvent, onDeleteEvent }) => {
  const y = month.getFullYear();
  const m = month.getMonth();
  const firstDay = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const today = new Date().toISOString().slice(0, 10);
  const monthName = month.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const getEventsForDay = (day) => {
    const dateStr = `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr);
  };

  const typeColor = { installation: 'bg-cyan-500', measurement: 'bg-purple-500', delivery: 'bg-green-500', meeting: 'bg-yellow-500', other: 'bg-gray-500' };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div><h2 className="text-3xl font-bold mb-1">Calendar</h2><p className="text-gray-500">Schedule & appointments</p></div>
        <button onClick={onAddEvent} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-3 rounded-xl hover:shadow-lg hover:shadow-cyan-500/30 transition-all flex items-center space-x-2 font-medium"><Plus size={20} /><span>Add Event</span></button>
      </div>
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => onMonthChange(new Date(y, m - 1, 1))} className="p-2 hover:bg-gray-800 rounded-lg"><ChevronLeft size={20} className="text-gray-400" /></button>
          <h3 className="text-xl font-bold">{monthName}</h3>
          <button onClick={() => onMonthChange(new Date(y, m + 1, 1))} className="p-2 hover:bg-gray-800 rounded-lg"><ChevronRight size={20} className="text-gray-400" /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <div key={d} className="text-center text-xs font-semibold text-gray-500 py-2">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, i) => {
            if (day === null) return <div key={`e${i}`} className="min-h-[80px]" />;
            const dateStr = `${y}-${String(m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayEvents = getEventsForDay(day);
            const isToday = dateStr === today;
            return (
              <div key={day} className={`min-h-[80px] p-1 rounded-lg border ${isToday ? 'border-cyan-500/50 bg-cyan-500/5' : 'border-gray-800 hover:border-gray-700'} transition-all`}>
                <div className={`text-xs font-medium mb-1 ${isToday ? 'text-cyan-400' : 'text-gray-400'}`}>{day}</div>
                {dayEvents.slice(0, 2).map(ev => (
                  <button key={ev.id} onClick={() => onEditEvent(ev)} className={`w-full text-left text-xs px-1 py-0.5 rounded mb-0.5 truncate text-white ${typeColor[ev.type] || typeColor.other}`}>{ev.timeStart ? ev.timeStart + ' ' : ''}{ev.title}</button>
                ))}
                {dayEvents.length > 2 && <div className="text-xs text-gray-500">+{dayEvents.length - 2} more</div>}
              </div>
            );
          })}
        </div>
      </div>
      {events.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-3">Upcoming Events</h3>
          <div className="space-y-2">
            {events.sort((a, b) => a.date.localeCompare(b.date)).map(ev => (
              <div key={ev.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex justify-between items-center hover:border-cyan-500/30 transition-all">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${typeColor[ev.type] || typeColor.other}`} />
                  <div>
                    <p className="font-medium text-sm">{ev.title}</p>
                    <p className="text-xs text-gray-500">{ev.date} {ev.timeStart && `at ${ev.timeStart}`} {ev.customerName && `· ${ev.customerName}`}</p>
                  </div>
                </div>
                <div className="flex space-x-1">
                  <button onClick={() => onEditEvent(ev)} className="text-cyan-400 hover:text-cyan-300 p-1"><Edit2 size={14} /></button>
                  <button onClick={() => onDeleteEvent(ev.id)} className="text-red-400 hover:text-red-300 p-1"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Reports Module
const ReportsModule = ({ data }) => {
  if (!data) return <div className="text-center py-16"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mx-auto mb-4" /><p className="text-gray-400">Loading reports...</p></div>;
  const maxRev = Math.max(...data.revenueByMonth.map(r => r.revenue), 1);
  const maxExp = Math.max(...data.expenseByCategory.map(r => r.total), 1);
  return (
    <div>
      <div className="mb-6"><h2 className="text-3xl font-bold mb-1">Reports & Analytics</h2><p className="text-gray-500">Business insights</p></div>
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5"><h3 className="text-gray-400 text-xs mb-1 uppercase">Avg Project Value</h3><p className="text-2xl font-bold text-cyan-400">${fmtMoney(data.avgProject.avg, 0)}</p></div>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5"><h3 className="text-gray-400 text-xs mb-1 uppercase">Estimate Conversion</h3><p className="text-2xl font-bold text-green-400">{data.estimateStats.conversionRate}%</p><p className="text-xs text-gray-500">{data.estimateStats.converted}/{data.estimateStats.total}</p></div>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5"><h3 className="text-gray-400 text-xs mb-1 uppercase">Low Stock Items</h3><p className="text-2xl font-bold text-red-400">{data.lowStock.length}</p></div>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5"><h3 className="text-gray-400 text-xs mb-1 uppercase">Upcoming Events</h3><p className="text-2xl font-bold text-purple-400">{data.upcomingEvents.length}</p></div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Revenue by month */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
          <h3 className="text-lg font-bold mb-4">Revenue by Month</h3>
          {data.revenueByMonth.length === 0 ? <p className="text-gray-500 text-center py-4">No completed projects yet</p> : (
            <div className="space-y-2">
              {data.revenueByMonth.slice().reverse().map(r => (
                <div key={r.month} className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 w-16">{r.month}</span>
                  <div className="flex-1 bg-gray-800 rounded-full h-6 overflow-hidden"><div className="bg-gradient-to-r from-cyan-500 to-blue-600 h-full rounded-full flex items-center px-2" style={{ width: `${Math.max((r.revenue / maxRev) * 100, 5)}%` }}><span className="text-xs font-medium text-white whitespace-nowrap">${fmtMoney(r.revenue, 0)}</span></div></div>
                  <span className="text-xs text-gray-500">{r.count} proj</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Top customers */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
          <h3 className="text-lg font-bold mb-4">Top Customers</h3>
          {data.topCustomers.length === 0 ? <p className="text-gray-500 text-center py-4">No data yet</p> : (
            <div className="space-y-3">
              {data.topCustomers.map((c, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-xl">
                  <div className="flex items-center gap-3"><span className="text-xs font-bold text-gray-500 w-5">#{i + 1}</span><span className="font-medium text-sm">{c.name}</span></div>
                  <div className="text-right"><span className="text-sm font-bold text-cyan-400">${fmtMoney(c.revenue, 0)}</span><span className="text-xs text-gray-500 ml-2">{c.projects} proj</span></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Expense categories */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
          <h3 className="text-lg font-bold mb-4">Expense Breakdown</h3>
          {data.expenseByCategory.length === 0 ? <p className="text-gray-500 text-center py-4">No expense data</p> : (
            <div className="space-y-2">
              {data.expenseByCategory.map((e, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 w-24 truncate">{e.category}</span>
                  <div className="flex-1 bg-gray-800 rounded-full h-5 overflow-hidden"><div className="bg-gradient-to-r from-red-500 to-orange-500 h-full rounded-full flex items-center px-2" style={{ width: `${Math.max((e.total / maxExp) * 100, 8)}%` }}><span className="text-xs font-medium text-white whitespace-nowrap">${fmtMoney(e.total, 0)}</span></div></div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Project status */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
          <h3 className="text-lg font-bold mb-4">Projects by Status</h3>
          {data.projectsByStatus.length === 0 ? <p className="text-gray-500 text-center py-4">No projects</p> : (
            <div className="space-y-3">
              {data.projectsByStatus.map((p, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-xl">
                  <span className="font-medium text-sm">{p.status}</span>
                  <div><span className="text-sm font-bold text-cyan-400">{p.count}</span><span className="text-xs text-gray-500 ml-2">${fmtMoney(p.total, 0)}</span></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Invoice stats and upcoming */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
          <h3 className="text-lg font-bold mb-4">Invoice Status</h3>
          {data.invoiceStats.length === 0 ? <p className="text-gray-500 text-center py-4">No invoices</p> : (
            <div className="space-y-3">{data.invoiceStats.map((s, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-xl"><span className="font-medium text-sm capitalize">{s.status}</span><div><span className="font-bold text-sm">{s.count}</span><span className="text-xs text-gray-500 ml-2">${fmtMoney(s.total, 0)}</span></div></div>
            ))}</div>
          )}
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
          <h3 className="text-lg font-bold mb-4">Upcoming (14 days)</h3>
          {data.upcomingEvents.length === 0 ? <p className="text-gray-500 text-center py-4">Nothing scheduled</p> : (
            <div className="space-y-2">{data.upcomingEvents.map((e, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-xl"><div><p className="font-medium text-sm">{e.title}</p><p className="text-xs text-gray-500">{e.date} {e.time && `at ${e.time}`}</p></div><span className="text-xs text-gray-400">{e.customer || ''}</span></div>
            ))}</div>
          )}
        </div>
      </div>
    </div>
  );
};

// Modal Component
const Modal = ({ type, item, onClose, onSave, customers, employees, projects, finCategories, finCounterparties, finBankAccounts }) => {
  const [formData, setFormData] = useState(item || {});
  const [cabinetItems, setCabinetItems] = useState(item?.items || []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (type === 'estimates') {
      const laborCost = (parseFloat(formData.laborHours) || 0) * (parseFloat(formData.laborRate) || 0);
      const materialCost = cabinetItems.reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0);
      onSave({
        ...formData,
        items: cabinetItems,
        totalCost: laborCost + materialCost
      });
    } else {
      onSave(formData);
    }
  };

  const addCabinetItem = () => {
    setCabinetItems([...cabinetItems, {
      type: '',
      quantity: 1,
      dimensions: '',
      material: '',
      finish: '',
      cost: 0
    }]);
  };

  const updateCabinetItem = (index, field, value) => {
    const updated = [...cabinetItems];
    updated[index][field] = value;
    setCabinetItems(updated);
  };

  const removeCabinetItem = (index) => {
    setCabinetItems(cabinetItems.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-100">
            {item ? 'Edit' : 'Add'} {type === 'customers' ? 'Customer' : type === 'employees' ? 'Employee' : type === 'users' ? 'User' : type === 'fin-transaction' ? 'Transaction' : type === 'fin-movement' ? 'Bank Movement' : type === 'fin-category' ? 'Category' : type === 'fin-counterparty' ? 'Counterparty' : type === 'fin-bank-account' ? 'Bank Account' : type === 'invoices' ? 'Invoice' : type === 'inventory' ? 'Inventory Item' : type === 'schedule' ? 'Event' : 'Estimate'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {type === 'customers' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Customer Type</label>
                <div className="flex rounded-xl overflow-hidden border border-gray-700">
                  <button type="button" onClick={() => setFormData({...formData, customerType: 'p2p'})}
                    className={`flex-1 py-3 text-sm font-medium transition-colors ${(formData.customerType || 'p2p') === 'p2p' ? 'bg-cyan-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                    Personal (P2P)
                  </button>
                  <button type="button" onClick={() => setFormData({...formData, customerType: 'business'})}
                    className={`flex-1 py-3 text-sm font-medium transition-colors ${formData.customerType === 'business' ? 'bg-cyan-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                    Business
                  </button>
                </div>
              </div>

              {formData.customerType === 'business' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Company Name *</label>
                    <input type="text" required value={formData.companyName || ''} onChange={(e) => setFormData({...formData, companyName: e.target.value, name: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="e.g., ABC Builders Inc." />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Contact Person</label>
                    <input type="text" value={formData.contactPerson || ''} onChange={(e) => setFormData({...formData, contactPerson: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="Main point of contact" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
                      <input type="tel" placeholder="xxx-xxx-xxxx" value={formatPhoneInput(formData.phone || '')} onChange={(e) => setFormData({...formData, phone: formatPhoneInput(e.target.value)})}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                      <input type="email" value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Tax ID / EIN</label>
                      <input type="text" value={formData.taxId || ''} onChange={(e) => setFormData({...formData, taxId: e.target.value})}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="XX-XXXXXXX" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Website</label>
                      <input type="url" value={formData.website || ''} onChange={(e) => setFormData({...formData, website: e.target.value})}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="https://example.com" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Industry</label>
                    <input type="text" value={formData.industry || ''} onChange={(e) => setFormData({...formData, industry: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="e.g., Construction, Real Estate" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Address</label>
                    <textarea value={formData.address || ''} onChange={(e) => setFormData({...formData, address: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" rows="2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Billing Address</label>
                    <textarea value={formData.billingAddress || ''} onChange={(e) => setFormData({...formData, billingAddress: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" rows="2" placeholder="If different from main address" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
                    <textarea value={formData.notes || ''} onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" rows="3" />
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">First Name *</label>
                      <input type="text" required value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Last Name</label>
                      <input type="text" value={formData.lastName || ''} onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Phone *</label>
                    <input type="tel" required placeholder="xxx-xxx-xxxx" value={formatPhoneInput(formData.phone || '')} onChange={(e) => setFormData({...formData, phone: formatPhoneInput(e.target.value)})}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Email *</label>
                    <input type="email" required value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Address *</label>
                    <textarea required value={formData.address || ''} onChange={(e) => setFormData({...formData, address: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" rows="2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
                    <textarea value={formData.notes || ''} onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" rows="3" />
                  </div>
                </>
              )}
            </div>
          )}

          {type === 'employees' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name || ''}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Role *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g., Cabinet Maker, Installer, Finisher"
                  value={formData.role || ''}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Phone *</label>
                <input
                  type="tel"
                  required
                  placeholder="xxx-xxx-xxxx"
                  value={formatPhoneInput(formData.phone || '')}
                  onChange={(e) => setFormData({...formData, phone: formatPhoneInput(e.target.value)})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Hourly Rate ($) *</label>
                <input
                  type="number"
                  required
                  step="0.01"
                  value={formData.hourlyRate || ''}
                  onChange={(e) => setFormData({...formData, hourlyRate: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
            </div>
          )}

          {type === 'users' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">First Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.firstName || ''}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={formData.lastName || ''}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
                <input
                  type="tel"
                  placeholder="xxx-xxx-xxxx"
                  value={formatPhoneInput(formData.phone || '')}
                  onChange={(e) => setFormData({...formData, phone: formatPhoneInput(e.target.value)})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Username *</label>
                <input
                  type="text"
                  required
                  value={formData.username || ''}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {item ? 'New Password (leave blank to keep current)' : 'Password * (min 8 characters)'}
                </label>
                <input
                  type="password"
                  required={!item}
                  minLength={8}
                  value={formData.password || ''}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder={item ? 'Leave blank to keep current password' : ''}
                />
              </div>
            </div>
          )}

          {type === 'fin-transaction' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Direction *</label>
                  <select value={formData.direction || 'out'} onChange={(e) => setFormData({...formData, direction: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                    <option value="in">Income (In)</option>
                    <option value="out">Expense (Out)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Date *</label>
                  <input type="date" required value={formData.date || ''} onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <input type="text" value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Amount *</label>
                  <input type="number" required step="0.01" value={formData.amount || ''} onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                  <select value={formData.status || 'pending'} onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                    <option value="pending">Pending</option>
                    <option value="partial">Partial</option>
                    <option value="settled">Settled</option>
                    <option value="canceled">Canceled</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                  <select value={formData.categoryId || ''} onChange={(e) => setFormData({...formData, categoryId: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                    <option value="">None</option>
                    {(finCategories||[]).map(c => <option key={c.id} value={c.id}>{c.name} ({c.type})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Counterparty</label>
                  <select value={formData.counterpartyId || ''} onChange={(e) => setFormData({...formData, counterpartyId: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                    <option value="">None</option>
                    {(finCounterparties||[]).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Bank Account</label>
                  <select value={formData.bankAccountId || ''} onChange={(e) => setFormData({...formData, bankAccountId: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                    <option value="">None</option>
                    {(finBankAccounts||[]).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Due Date</label>
                  <input type="date" value={formData.dueDate || ''} onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
                <textarea value={formData.notes || ''} onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" rows="2" />
              </div>
            </div>
          )}

          {type === 'fin-movement' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Bank Account *</label>
                <select required value={formData.bankAccountId || ''} onChange={(e) => setFormData({...formData, bankAccountId: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                  <option value="">Select bank account</option>
                  {(finBankAccounts||[]).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Date *</label>
                  <input type="date" required value={formData.date || ''} onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Amount *</label>
                  <input type="number" required step="0.01" value={formData.amount || ''} onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="Positive for deposit, negative for withdrawal" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <input type="text" value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">External ID (optional)</label>
                <input type="text" value={formData.externalId || ''} onChange={(e) => setFormData({...formData, externalId: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="Reference from bank statement" />
              </div>
            </div>
          )}

          {type === 'fin-category' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Name *</label>
                <input type="text" required value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="e.g., Materials, Labor, Utilities" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Type *</label>
                <select value={formData.type || 'expense'} onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                  <option value="expense">Expense</option>
                  <option value="income">Income</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Color (optional)</label>
                <input type="text" value={formData.color || ''} onChange={(e) => setFormData({...formData, color: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="#3b82f6" />
              </div>
            </div>
          )}

          {type === 'fin-counterparty' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Name *</label>
                <input type="text" required value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Type</label>
                <select value={formData.type || 'other'} onChange={(e) => setFormData({...formData, type: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                  <option value="customer">Customer</option>
                  <option value="vendor">Vendor</option>
                  <option value="employee">Employee</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <input type="email" value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
                  <input type="tel" placeholder="xxx-xxx-xxxx" value={formatPhoneInput(formData.phone || '')} onChange={(e) => setFormData({...formData, phone: formatPhoneInput(e.target.value)})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
                <textarea value={formData.notes || ''} onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" rows="2" />
              </div>
            </div>
          )}

          {type === 'fin-bank-account' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Account Name *</label>
                <input type="text" required value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="e.g., Business Checking" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Institution</label>
                  <input type="text" value={formData.institution || ''} onChange={(e) => setFormData({...formData, institution: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="e.g., Chase, Bank of America" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Last 4 Digits</label>
                  <input type="text" maxLength={4} value={formData.last4 || ''} onChange={(e) => setFormData({...formData, last4: e.target.value.replace(/\D/g, '').slice(0, 4)})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="1234" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Currency</label>
                  <select value={formData.currency || 'USD'} onChange={(e) => setFormData({...formData, currency: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="MXN">MXN</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Opening Balance</label>
                  <input type="number" step="0.01" value={formData.openingBalance || ''} onChange={(e) => setFormData({...formData, openingBalance: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="0.00" />
                </div>
              </div>
            </div>
          )}

          {type === 'invoices' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Customer</label>
                <select value={formData.customerId || ''} onChange={(e) => {
                  const customer = customers.find(c => c.id === e.target.value);
                  setFormData({...formData, customerId: e.target.value, customerName: customer ? (customer.customerType === 'business' ? customer.companyName || customer.name : `${customer.name} ${customer.lastName || ''}`.trim()) : ''});
                }} className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                  <option value="">Select a customer</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.customerType === 'business' ? c.companyName || c.name : `${c.name} ${c.lastName || ''}`.trim()}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Issue Date *</label>
                  <input type="date" required value={formData.issueDate || ''} onChange={(e) => setFormData({...formData, issueDate: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Due Date</label>
                  <input type="date" value={formData.dueDate || ''} onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Subtotal *</label>
                  <input type="number" required step="0.01" value={formData.subtotal || ''} onChange={(e) => setFormData({...formData, subtotal: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="0.00" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Tax Rate (%)</label>
                  <input type="number" step="0.01" value={formData.taxRate || ''} onChange={(e) => setFormData({...formData, taxRate: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="0" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Project</label>
                <select value={formData.projectId || ''} onChange={(e) => setFormData({...formData, projectId: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                  <option value="">None</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name || p.description}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                <select value={formData.status || 'draft'} onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
                <textarea value={formData.notes || ''} onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" rows="2" placeholder="Additional notes..." />
              </div>
            </div>
          )}

          {type === 'inventory' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Name *</label>
                  <input type="text" required value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="e.g., Oak Plywood Sheet" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">SKU</label>
                  <input type="text" value={formData.sku || ''} onChange={(e) => setFormData({...formData, sku: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="e.g., OAK-PLY-001" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                  <input type="text" value={formData.category || ''} onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="e.g., Wood" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Unit</label>
                  <select value={formData.unit || 'pcs'} onChange={(e) => setFormData({...formData, unit: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                    <option value="pcs">Pieces</option>
                    <option value="sheets">Sheets</option>
                    <option value="ft">Feet</option>
                    <option value="sqft">Sq Feet</option>
                    <option value="lbs">Pounds</option>
                    <option value="gal">Gallons</option>
                    <option value="box">Boxes</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Cost/Unit</label>
                  <input type="number" step="0.01" value={formData.costPerUnit || ''} onChange={(e) => setFormData({...formData, costPerUnit: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="0.00" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Quantity</label>
                  <input type="number" step="0.01" value={formData.quantity || ''} onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="0" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Min Quantity (Alert)</label>
                  <input type="number" step="0.01" value={formData.minQuantity || ''} onChange={(e) => setFormData({...formData, minQuantity: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="0" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Supplier</label>
                  <input type="text" value={formData.supplier || ''} onChange={(e) => setFormData({...formData, supplier: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="Supplier name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Location</label>
                  <input type="text" value={formData.location || ''} onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="e.g., Warehouse A, Shelf 3" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
                <textarea value={formData.notes || ''} onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" rows="2" placeholder="Additional notes..." />
              </div>
            </div>
          )}

          {type === 'schedule' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Title *</label>
                <input type="text" required value={formData.title || ''} onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="e.g., Kitchen Cabinet Installation" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Type</label>
                  <select value={formData.type || 'installation'} onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                    <option value="installation">Installation</option>
                    <option value="measurement">Measurement</option>
                    <option value="delivery">Delivery</option>
                    <option value="consultation">Consultation</option>
                    <option value="repair">Repair</option>
                    <option value="meeting">Meeting</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                  <select value={formData.status || 'scheduled'} onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                    <option value="scheduled">Scheduled</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Date *</label>
                  <input type="date" required value={formData.date || ''} onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Start Time</label>
                  <input type="time" value={formData.timeStart || ''} onChange={(e) => setFormData({...formData, timeStart: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">End Time</label>
                  <input type="time" value={formData.timeEnd || ''} onChange={(e) => setFormData({...formData, timeEnd: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Customer</label>
                  <select value={formData.customerId || ''} onChange={(e) => {
                    const customer = customers.find(c => c.id === e.target.value);
                    setFormData({...formData, customerId: e.target.value, customerName: customer ? (customer.customerType === 'business' ? customer.companyName || customer.name : `${customer.name} ${customer.lastName || ''}`.trim()) : ''});
                  }} className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                    <option value="">None</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.customerType === 'business' ? c.companyName || c.name : `${c.name} ${c.lastName || ''}`.trim()}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Project</label>
                  <select value={formData.projectId || ''} onChange={(e) => setFormData({...formData, projectId: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                    <option value="">None</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name || p.description}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Assigned Employees</label>
                <div className="flex flex-wrap gap-2 p-3 bg-gray-800 border border-gray-700 rounded-xl min-h-[48px]">
                  {employees.map(emp => (
                    <label key={emp.id} className="flex items-center gap-1 px-2 py-1 bg-gray-700 rounded-lg cursor-pointer text-sm text-gray-300 hover:bg-gray-600">
                      <input type="checkbox" checked={(formData.employeeIds || []).includes(emp.id)}
                        onChange={(e) => {
                          const ids = formData.employeeIds || [];
                          setFormData({...formData, employeeIds: e.target.checked ? [...ids, emp.id] : ids.filter(id => id !== emp.id)});
                        }} className="rounded border-gray-600" />
                      {emp.name}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Address</label>
                <input type="text" value={formData.address || ''} onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" placeholder="Job site address" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Notes</label>
                <textarea value={formData.notes || ''} onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent" rows="2" placeholder="Additional notes..." />
              </div>
            </div>
          )}

          {type === 'estimates' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Customer *</label>
                <select
                  required
                  value={formData.customerId || ''}
                  onChange={(e) => {
                    const customer = customers.find(c => c.id === e.target.value);
                    setFormData({
                      ...formData, 
                      customerId: e.target.value,
                      customerName: customer ? (customer.customerType === 'business' ? customer.companyName || customer.name : `${customer.name} ${customer.lastName || ''}`.trim()) : ''
                    });
                  }}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                >
                  <option value="">Select a customer</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>{customer.customerType === 'business' ? customer.companyName || customer.name : `${customer.name} ${customer.lastName || ''}`.trim()}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Project Description *</label>
                <textarea
                  required
                  value={formData.description || ''}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  rows="2"
                  placeholder="e.g., Kitchen cabinet installation"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Labor Hours *</label>
                  <input
                    type="number"
                    required
                    step="0.5"
                    value={formData.laborHours || ''}
                    onChange={(e) => setFormData({...formData, laborHours: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Labor Rate ($/hr) *</label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    value={formData.laborRate || ''}
                    onChange={(e) => setFormData({...formData, laborRate: e.target.value})}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Installation Date *</label>
                <input
                  type="date"
                  required
                  value={formData.installationDate || ''}
                  onChange={(e) => setFormData({...formData, installationDate: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>

              <div className="border-t border-gray-800 pt-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-medium text-gray-300">Estimate Items</h3>
                  <button
                    type="button"
                    onClick={addCabinetItem}
                    className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center space-x-1 font-medium"
                  >
                    <Plus size={16} />
                    <span>Add Item</span>
                  </button>
                </div>
                
                {cabinetItems.map((item, index) => (
                  <div key={index} className="bg-gray-800/50 rounded-xl p-4 mb-3 border border-gray-700">
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Type</label>
                        <input
                          type="text"
                          placeholder="e.g., Base Cabinet"
                          value={item.type || ''}
                          onChange={(e) => updateCabinetItem(index, 'type', e.target.value)}
                          className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:ring-2 focus:ring-cyan-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Quantity</label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity || 1}
                          onChange={(e) => updateCabinetItem(index, 'quantity', e.target.value)}
                          className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:ring-2 focus:ring-cyan-500"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Dimensions</label>
                        <input
                          type="text"
                          placeholder="e.g., 24x36x12"
                          value={item.dimensions || ''}
                          onChange={(e) => updateCabinetItem(index, 'dimensions', e.target.value)}
                          className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:ring-2 focus:ring-cyan-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Material</label>
                        <input
                          type="text"
                          placeholder="e.g., MDF"
                          value={item.material || ''}
                          onChange={(e) => updateCabinetItem(index, 'material', e.target.value)}
                          className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:ring-2 focus:ring-cyan-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Finish</label>
                        <input
                          type="text"
                          placeholder="e.g., White"
                          value={item.finish || ''}
                          onChange={(e) => updateCabinetItem(index, 'finish', e.target.value)}
                          className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:ring-2 focus:ring-cyan-500"
                        />
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="flex-1 mr-2">
                        <label className="block text-xs text-gray-400 mb-1">Cost ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={item.cost || 0}
                          onChange={(e) => updateCabinetItem(index, 'cost', e.target.value)}
                          className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:ring-2 focus:ring-cyan-500"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeCabinetItem(index)}
                        className="mt-5 text-red-400 hover:text-red-300"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-700 rounded-xl text-gray-300 hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl hover:shadow-lg hover:shadow-cyan-500/30 transition-all flex items-center space-x-2 font-medium"
            >
              <Save size={18} />
              <span>Save</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MobelCabinetManager;